import React, { useCallback, useEffect, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { isQuickChargeOrderCode } from 'shared-models/browser';
import type { PrinterView, ReceiptView } from 'shared-models/browser';
import { printersService } from '../../../../../services/printers/printers.service';
import { receiptService } from '../../../../../services/receipt/receipt.service';
import { captureReceiptSnapshot } from '../shared/receipt.utils';
import { TransactionReceiptContent } from './TransactionReceiptContent';
import { TransactionReceiptModal } from './TransactionReceiptModal';
import { PickListModal } from '../../../../../components/PickListModal/PickListModal';
import { toastReceiptPrintResults } from './receipt-print-toast';
import { printableJobs, receiptsForPrinting, type ReceiptPrintJob } from './receipt-split.util';
import { resolveFixedPrinter, resolveReceiptPrintTarget } from '../../../hooks/fixed-printer.util';
import classes from './transaction-receipt.module.scss';

// clientClickAt is set only on the partial-receipt path; anchors backend print-latency queries.
// actionIdBase (auto full-close only) rides the backend single-winner claim across devices.
type HeadlessJob = { jobs: ReceiptPrintJob[]; orderId: number; printerId: number; key: string; clientClickAt?: string; actionIdBase?: string };
// Shown UI: every full close opens the postPayment success screen.
type FallbackJob = { orderId: number; key: string; variant: 'postPayment' };
// Take-away full close with no resolvable printer opens a bare printer picker before its success screen.
type PickerJob = { orderId: number; key: string; receipt: ReceiptView; printers: PrinterView[]; clientClickAt?: string; showPostPayment?: boolean; actionIdBase?: string };

const jobKey = (orderId: number, paymentlineId?: number) => `${orderId}:${paymentlineId ?? 'full'}`;

// Same dispatch toast the full receipt shows on print, reused when a partial print is queued.
const notifyReceiptDispatched = () =>
  notifications.show({ color: 'green', title: 'הדפסה', message: 'הקבלה נשלחה להדפסה', autoClose: 3000 });

// Returns a request fn plus the JSX (offscreen capture node + fallback picker modal) to mount once.
export function useTransactionReceiptAutoPrint(
  tenantId: string,
  employeeSession: string | null,
  onNewOrder?: () => void,
) {
  const offscreenRefs = useRef<Array<HTMLDivElement | null>>([]);
  const inFlightRef = useRef<Set<string>>(new Set());
  const pendingRef = useRef<HeadlessJob[]>([]);
  const activeKeyRef = useRef<string | null>(null);
  const manualCounterRef = useRef(0);
  const [headlessJob, setHeadlessJob] = useState<HeadlessJob | null>(null);
  const [fallback, setFallback] = useState<FallbackJob | null>(null);
  const [pickerJob, setPickerJob] = useState<PickerJob | null>(null);
  // Bridges the async gap from order-close to fallback/picker mount so the zone stays unsafe.
  const autoPrintInFlightRef = useRef(0);
  const [autoPrintInFlight, setAutoPrintInFlight] = useState(false);

  const beginAutoPrintInFlight = useCallback(() => {
    autoPrintInFlightRef.current += 1;
    setAutoPrintInFlight(true);
  }, []);

  const endAutoPrintInFlight = useCallback(() => {
    autoPrintInFlightRef.current = Math.max(0, autoPrintInFlightRef.current - 1);
    if (autoPrintInFlightRef.current === 0) setAutoPrintInFlight(false);
  }, []);

  // FIFO pump: start next queued job only when idle so a manual print never clobbers an auto one.
  const takeNext = useCallback(() => {
    const next = pendingRef.current.shift() ?? null;
    activeKeyRef.current = next?.key ?? null;
    setHeadlessJob(next);
  }, []);

  const enqueueHeadless = useCallback((job: HeadlessJob) => {
    pendingRef.current.push(job);
    if (!activeKeyRef.current) takeNext();
  }, [takeNext]);

  // Manual reprint from the modal: same headless pipeline, no dedupe (operator may reprint).
  const queueHeadlessReceiptPrint = useCallback(
    (orderId: number, receipt: ReceiptView, printerId: number, selectedJobIds?: readonly string[]) => {
      manualCounterRef.current += 1;
      const key = `manual:${orderId}:${manualCounterRef.current}`;
      const selectedJobIdSet = selectedJobIds ? new Set(selectedJobIds) : null;
      enqueueHeadless({
        // Selections may name the summary copy, which is not part of the default run.
        jobs: selectedJobIdSet
          ? printableJobs(receipt).filter((job) => selectedJobIdSet.has(job.id))
          : receiptsForPrinting(receipt),
        orderId,
        printerId,
        key,
      });
    },
    [enqueueHeadless],
  );

  // Full close (any order type) opens the post-payment success screen; partial receipts print headless, no UI.
  const requestReceiptAutoPrint = useCallback(
    async (orderId: number, options?: { paymentlineId?: number; cashDrawerPrinterId?: number }) => {
      if (!employeeSession) return;
      const paymentlineId = options?.paymentlineId;
      const cashDrawerPrinterId = options?.cashDrawerPrinterId;
      // Anchor latency at the click for the partial path only; full closes send nothing.
      const clientClickAt = paymentlineId != null ? new Date().toISOString() : undefined;
      const key = jobKey(orderId, paymentlineId);
      if (inFlightRef.current.has(key)) return;
      inFlightRef.current.add(key);
      // Synchronous (pre-await) so the safe zone never reads open between close and modal-mount.
      beginAutoPrintInFlight();

      try {
        const [printers, receipt] = await Promise.all([
          printersService.listActive(tenantId, employeeSession),
          receiptService.getReceipt(tenantId, orderId, employeeSession, paymentlineId),
        ]);

        // Dine-in + quick-charge full close → success screen; plain take-away → silent auto-print.
        const isQuickCharge = isQuickChargeOrderCode(receipt.orderCode);
        const isTakeAwayFullClose = paymentlineId == null && receipt.orderType === 'take_away' && !isQuickCharge;
        if (paymentlineId == null && !isTakeAwayFullClose) {
          setFallback({ orderId, key, variant: 'postPayment' });
          return;
        }

        // Deterministic per-order key: the backend claim collapses the same close receipt fired
        // concurrently by multiple devices (initiator + resolved-SSE recovery) to one job.
        const actionIdBase = isTakeAwayFullClose ? `receipt-close-${orderId}` : undefined;
        const target = resolveReceiptPrintTarget(printers);
        const printerId = target.kind === 'print'
          ? target.printerId
          : resolveFixedPrinter(printers) == null
            ? cashDrawerPrinterId
            : null;
        if (printerId != null) {
          enqueueHeadless({ jobs: receiptsForPrinting(receipt), orderId, printerId, key, clientClickAt, actionIdBase });
          if (isTakeAwayFullClose) {
            setFallback({ orderId, key, variant: 'postPayment' });
          }
          if (receipt.isPartial) notifyReceiptDispatched();
        } else if (target.kind === 'pick') {
          setPickerJob({ orderId, key, receipt, printers: target.candidates ?? printers, clientClickAt, showPostPayment: isTakeAwayFullClose, actionIdBase });
        } else {
          // No printer at all — a picker over an empty list is useless, so dead-end with a notice.
          inFlightRef.current.delete(key);
          notifications.show({ color: 'red', title: 'אין מדפסת פעילה', message: 'לא נמצאה מדפסת להדפסת הקבלה', autoClose: 3000 });
          if (isTakeAwayFullClose) {
            setFallback({ orderId, key, variant: 'postPayment' });
          }
        }
      } catch (err) {
        inFlightRef.current.delete(key);
        console.error('Receipt auto-print failed:', err);
      } finally {
        endAutoPrintInFlight();
      }
    },
    [tenantId, employeeSession, enqueueHeadless, beginAutoPrintInFlight, endAutoPrintInFlight],
  );

  useEffect(() => {
    if (!headlessJob || !employeeSession) return;
    let cancelled = false;
    const job = headlessJob;
    void (async () => {
      try {
        const jobIds: number[] = [];
        for (let i = 0; i < job.jobs.length; i += 1) {
          const node = offscreenRefs.current[i];
          const image = node ? await captureReceiptSnapshot(node) : undefined;
          if (cancelled) return;
          if (!image) continue;
          // Per-document key (payment slice / final summary) so one close's own documents all pass.
          const receiptActionId = job.actionIdBase
            ? `${job.actionIdBase}:${(job.jobs[i].id ?? String(i)).replace(/[^A-Za-z0-9-]/g, '-')}`
            : undefined;
          // Only the partial path carries a click anchor; full/manual prints send nothing extra.
          const jobId = receiptActionId
            ? await printersService.queueOrderReceiptPrint(
                tenantId,
                job.orderId,
                job.printerId,
                image,
                employeeSession,
                false,
                job.clientClickAt,
                receiptActionId,
              )
            : job.clientClickAt
              ? await printersService.queueOrderReceiptPrint(
                  tenantId,
                  job.orderId,
                  job.printerId,
                  image,
                  employeeSession,
                  false,
                  job.clientClickAt,
                )
              : await printersService.queueOrderReceiptPrint(
                  tenantId,
                  job.orderId,
                  job.printerId,
                  image,
                  employeeSession,
                );
          if (jobId != null) jobIds.push(jobId);
        }
        if (jobIds.length > 0) {
          void toastReceiptPrintResults(tenantId, jobIds, employeeSession);
        }
      } catch (err) {
        console.error('Receipt auto-print queue failed:', err);
        notifications.show({ color: 'red', title: 'ההדפסה נכשלה', message: 'שגיאה בשליחת הקבלה להדפסה', autoClose: 3000 });
      } finally {
        inFlightRef.current.delete(job.key);
        if (!cancelled) {
          takeNext();
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [headlessJob, tenantId, employeeSession, takeNext]);

  const closeFallback = useCallback(() => {
    setFallback((current) => {
      if (current) inFlightRef.current.delete(current.key);
      return null;
    });
  }, []);

  // Picker pick → headless print; cancel → release the in-flight guard and print nothing.
  const handlePickerPrint = useCallback((printerId: number) => {
    setPickerJob((current) => {
      if (current) {
        enqueueHeadless({ jobs: receiptsForPrinting(current.receipt), orderId: current.orderId, printerId, key: current.key, clientClickAt: current.clientClickAt, actionIdBase: current.actionIdBase });
        if (current.showPostPayment) {
          setFallback({ orderId: current.orderId, key: current.key, variant: 'postPayment' });
        }
        if (current.receipt.isPartial) notifyReceiptDispatched();
      }
      return null;
    });
  }, [enqueueHeadless]);

  const closePicker = useCallback(() => {
    setPickerJob((current) => {
      if (current) inFlightRef.current.delete(current.key);
      return null;
    });
  }, []);

  const autoPrintElements = (
    <>
      {headlessJob && (
        <div className={classes.printCaptureOffscreenRow}>
          {headlessJob.jobs.map((job, index) => (
            <div
              key={index}
              className={classes.offscreenReceiptReady}
              ref={(el) => { offscreenRefs.current[index] = el; }}
            >
              <TransactionReceiptContent receipt={job.receipt} title={job.title} showPaymentDetails={job.showPaymentDetails} />
            </div>
          ))}
        </div>
      )}
      {fallback && (
        <TransactionReceiptModal
          opened
          onClose={closeFallback}
          tenantId={tenantId}
          orderId={fallback.orderId}
          variant={fallback.variant}
          onNewOrder={onNewOrder}
          onHeadlessPrint={(receipt, printerId, selectedJobIds) => queueHeadlessReceiptPrint(
            fallback.orderId,
            receipt,
            printerId,
            selectedJobIds,
          )}
        />
      )}
      <PickListModal
        title="בחר מדפסת"
        options={pickerJob?.printers ?? null}
        disabled={false}
        optionColor="primary"
        dataTestId="receipt-printer-pick"
        onPick={handlePickerPrint}
        onClose={closePicker}
      />
    </>
  );

  // Both post-payment surfaces (success fallback + bare printer picker) defer a hard refresh.
  return { requestReceiptAutoPrint, queueHeadlessReceiptPrint, autoPrintElements, closeFallback, isPostPaymentScreenOpen: fallback !== null || pickerJob !== null || autoPrintInFlight };
}
